import { useCallback } from "react";
import React, { useState, useRef, useEffect } from "react";
import { MultiFactorResolver, TotpMultiFactorGenerator } from "firebase/auth";
import { ArrowLeft, AlertCircle, X } from "lucide-react";

interface MfaVerifyPageProps {
  resolver: MultiFactorResolver | null;
  onVerificationComplete: () => void;
  onCancel: () => void;
  cancelLabel?: string;
}

const MfaVerifyPage: React.FC<MfaVerifyPageProps> = ({
  resolver,
  onVerificationComplete,
  onCancel,
  cancelLabel = "Back to Login",
}) => {
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const [error, setError] = useState<string>("");
  const [verifying, setVerifying] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // Auto-verify when 6 digits are entered
  const handleVerifyMfa = useCallback(async (e?: React.FormEvent, codeOverride?: string) => {
    if (e) e.preventDefault();

    const currentCode = codeOverride || otp.join("");

    if (!resolver) {
      setError("MFA resolver not available.");
      return;
    }

    if (currentCode.length !== 6) {
      if (e) setError("Please enter the full 6-digit code.");
      return;
    }

    setVerifying(true);
    setError("");

    try {
      const totpHint = resolver.hints.find(
        (hint) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID
      );

      if (!totpHint) {
        setError("No authenticator found.");
        return;
      }

      const assertion = TotpMultiFactorGenerator.assertionForSignIn(
        totpHint.uid,
        currentCode
      );

      await resolver.resolveSignIn(assertion);
      onVerificationComplete();
    } catch (err: any) {
      setError("Invalid verification code. Please try again.");
      console.error("MFA Verification Error:", err);
      setVerifying(false);
      
      // Clear inputs on error and focus first box
      setOtp(new Array(6).fill(""));
      if (inputRefs.current[0]) inputRefs.current[0].focus();
    }
  }, [otp, resolver, onVerificationComplete]);

useEffect(() => {
    const code = otp.join("");
    if (code.length === 6 && !verifying) {
      handleVerifyMfa(undefined, code);
    }
  }, [otp, verifying, handleVerifyMfa]);



  const handleChange = (element: HTMLInputElement, index: number) => {
    if (isNaN(Number(element.value))) return false;
    const newOtp = [...otp];
    newOtp[index] = element.value.substring(element.value.length - 1);
    setOtp(newOtp);
    
    if (element.value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      } else {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, '').slice(0, 6).split("");
    
    if (pastedData.length > 0) {
      const newOtp = [...otp];
      pastedData.forEach((val, i) => {
        if (i < 6) newOtp[i] = val;
      });
      setOtp(newOtp);
      
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  return (
    <div className="w-full animate-in fade-in duration-300">
      {/* Back Button */}
      <button
        onClick={onCancel}
        type="button"
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-6 font-medium group focus:outline-none"
      >
        <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
        {cancelLabel}
      </button>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2 font-poppins">2-Step Verification</h1>
        <p className="text-gray-500 text-sm">Enter the 6-digit code from your authenticator app.</p>
      </div>

      {/* Modern Tailwind Error Banner */}
      {error && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 flex-1 leading-relaxed">
            {error}
          </p>
          <button
            onClick={() => setError("")}
            className="text-red-400 hover:text-red-600 transition-colors focus:outline-none p-0.5"
            aria-label="Dismiss error"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleVerifyMfa} className="w-full">
        <div className="flex justify-between gap-2 sm:gap-3 mb-8">
          {otp.map((data, index) => (
            <input
              key={index}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={1}
              value={data}
              ref={(el) => (inputRefs.current[index] = el)}
              onChange={(e) => handleChange(e.target, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              disabled={verifying}
              className={`w-12 h-14 sm:w-14 sm:h-16 text-center text-xl sm:text-2xl font-semibold bg-white rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#7E57C2]/20 focus:border-[#7E57C2] text-gray-900 disabled:bg-gray-50 disabled:text-gray-400 ${
                error ? "border-red-400" : "border-gray-200"
              }`}
            />
          ))}
        </div>

        <button
          type="submit"
          disabled={verifying || otp.join("").length < 6}
          className="w-full h-12 bg-[#7E57C2] hover:bg-[#6b46ac] text-white rounded-xl text-[15px] font-medium transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center shadow-sm hover:shadow-md"
        >
          {verifying ? "Verifying..." : "Verify"}
        </button>
      </form>
    </div>
  );
};

export default MfaVerifyPage;
